/**
 * Converter: FSMDefinition → StateMachine manifest entries.
 *
 * Maps the rich FSMDefinition type from fsmRegistry.ts to the simpler
 * StateMachine type used in the AtlasManifest. This allows manifest-consuming
 * views to access FSM data alongside parsed manifest data.
 *
 * @see docs/impl/145 — FSM architecture
 * @see packages/atlas-manifest/src/types.ts — StateMachine type
 * @see internal/atlas/frontend/src/state/fsmRegistry.ts — FSMDefinition registry
 */
import type { StateMachine, StateMachineTransition } from '@atlas-manifest/types';
import type { FSMDefinition } from './fsmRegistry.js';

/**
 * Convert a single FSMDefinition to a StateMachine manifest entry.
 *
 * Maps:
 *   FSMTransition.event → StateMachineTransition.triggeredBy
 *   unique(FSMTransition.event) → StateMachine.triggeredByEvents (input events)
 *   FSMDefinition.emitsEvents is output events — NOT mapped to triggeredByEvents
 *   FSMDefinition.(safetyTier, sandboxed, readOnly) → optional fields
 *
 * Note: mutatesEntities and surfaces are not available in FSMDefinition,
 * so they default to empty arrays. These can be populated by manifest parsing
 * or domain-level configuration.
 *
 * Safety tier mapping: FSMDefinition.safetyTier is 0 | 1 | 2 | 3 (EventSafetyTier),
 * but StateMachine.safetyTier is 'T0' | 'T1' for the machine as a whole.
 * We map: 0 → 'T0', anything >= 1 → 'T1'.
 */
export function fsmToManifestEntry(fsm: FSMDefinition): StateMachine {
  const safetyTier = fsm.safetyTier === 0 ? 'T0' : 'T1';

  return {
    id: fsm.id,
    name: fsm.label,
    domain: '', // FSMDefinition does not specify a domain; will be set by caller or manifest
    states: fsm.states.map((s) => s.id),
    transitions: fsm.transitions.map(
      (t): StateMachineTransition => ({
        from: t.from,
        to: t.to,
        triggeredBy: t.event,
        guard: t.guard,
      }),
    ),
    triggeredByEvents: [...new Set(fsm.transitions.map(t => t.event))],
    mutatesEntities: [],
    surfaces: [],
    safetyTier,
    sandboxed: fsm.sandboxed,
    readOnly: fsm.readOnly,
  };
}

/**
 * Convert all FSMDefinitions in a registry to StateMachine manifest entries.
 */
export function fsmRegistryToManifestEntries(
  registry: readonly FSMDefinition[],
): StateMachine[] {
  return registry.map(fsmToManifestEntry);
}

/**
 * Merge manifest and registry StateMachine entries.
 *
 * Merge strategy:
 *   - Manifest entries (presumably from HTML doc parsing) take precedence
 *   - Registry entries fill in machines not present in the manifest
 *   - Result is keyed by id, with no duplicates
 *
 * This allows a manifest view to show both parsed (manifest) and hardcoded
 * (registry) FSM data without duplication.
 */
export function mergeStateMachines(
  fromManifest: StateMachine[],
  fromRegistry: StateMachine[],
): StateMachine[] {
  const byId = new Map<string, StateMachine>();

  // Manifest entries first (they take precedence)
  fromManifest.forEach((m) => {
    byId.set(m.id, m);
  });

  // Registry entries fill gaps
  fromRegistry.forEach((r) => {
    if (!byId.has(r.id)) {
      byId.set(r.id, r);
    }
  });

  return Array.from(byId.values());
}
